/**
 * CuttySark 賞ステージ画面の効果音（ドラムロールとファンファーレ）。
 *
 * 音源ファイルを持たず WebAudio で合成する（アセットと依存を増やさないため）。ブラウザは利用者の
 * 操作なしに音を鳴らせないので、画面の「音を出す」ボタンを押したときに AudioContext を作る。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business CuttySark 賞ステージ画面のドラムロールとファンファーレを WebAudio で合成して鳴らす
 * @graph-connects none
 */

/** 効果音を鳴らす口 @graph-connects none */
export interface StageSound {
  /** ドラムロール（ノイズを細かく刻む）。 */
  drumroll: (durationMs: number) => void;
  /** 1 位用のドラムロール（だんだん速く大きくなり、低い連打とシンバルで締める）。 */
  grandDrumroll: (durationMs: number) => void;
  /** ファンファーレ（3 和音の上昇）。 */
  fanfare: () => void;
  /** 1 位用のファンファーレ（シンバル・2 段の駆け上がり・オクターブを重ねた長い和音）。 */
  grandFanfare: () => void;
  /** 音を出す設定にしたときの確認音（短い 2 音）。 */
  chime: () => void;
  /** 代表者紹介・発表者が変わったときの登場音（短い上昇音）。 */
  entrance: () => void;
  /** AudioContext を閉じる（作り直す前・画面を閉じるときに呼ぶ）。 */
  close: () => void;
}

/**
 * 効果音を作る。WebAudio が使えない環境では何も鳴らさない口を返す。
 * @graph-connects none
 */
export function createStageSound(): StageSound {
  const AudioContextClass = globalThis.AudioContext as typeof AudioContext | undefined;
  if (AudioContextClass === undefined) {
    return {
      drumroll: () => undefined,
      grandDrumroll: () => undefined,
      fanfare: () => undefined,
      grandFanfare: () => undefined,
      chime: () => undefined,
      entrance: () => undefined,
      close: () => undefined,
    };
  }
  const context = new AudioContextClass();
  // ブラウザによっては、ボタン操作の中で作っても suspended で始まる（Safari・自動再生ポリシー）。
  // 作った直後に resume して、以降の効果音が無音にならないようにする
  void context.resume();

  /** 短いノイズを 1 発鳴らす */
  const hit = (at: number, gainValue: number): void => {
    const length = Math.floor(context.sampleRate * 0.05);
    const buffer = context.createBuffer(1, length, context.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < length; i += 1) data[i] = (Math.random() * 2 - 1) * (1 - i / length);
    const source = context.createBufferSource();
    source.buffer = buffer;
    const gain = context.createGain();
    gain.gain.value = gainValue;
    source.connect(gain).connect(context.destination);
    source.start(at);
  };

  /** シンバル（高い音だけを残した長めのノイズ） */
  const crash = (at: number, gainValue: number): void => {
    const length = Math.floor(context.sampleRate * 1.8);
    const buffer = context.createBuffer(1, length, context.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < length; i += 1) data[i] = (Math.random() * 2 - 1) * (1 - i / length) ** 3;
    const source = context.createBufferSource();
    source.buffer = buffer;
    const filter = context.createBiquadFilter();
    filter.type = 'highpass';
    filter.frequency.value = 5000;
    const gain = context.createGain();
    gain.gain.value = gainValue;
    source.connect(filter).connect(gain).connect(context.destination);
    source.start(at);
  };

  /** 1 音を鳴らす */
  const tone = (frequency: number, at: number, durationSec: number, peak = 0.18): void => {
    const oscillator = context.createOscillator();
    oscillator.type = 'triangle';
    oscillator.frequency.value = frequency;
    const gain = context.createGain();
    gain.gain.setValueAtTime(peak, at);
    gain.gain.exponentialRampToValueAtTime(0.001, at + durationSec);
    oscillator.connect(gain).connect(context.destination);
    oscillator.start(at);
    oscillator.stop(at + durationSec);
  };

  return {
    drumroll: (durationMs) => {
      const start = context.currentTime;
      const hits = Math.floor(durationMs / 45);
      for (let i = 0; i < hits; i += 1) hit(start + i * 0.045, 0.15 + (i / hits) * 0.35);
      hit(start + durationMs / 1000, 0.9);
    },
    grandDrumroll: (durationMs) => {
      const start = context.currentTime;
      const end = durationMs / 1000;
      // 間隔を 90ms から 30ms へ詰めながら、音量も上げていく
      for (let t = 0; t < end; ) {
        const progress = t / end;
        hit(start + t, 0.1 + progress * 0.6);
        t += 0.09 - progress * 0.06;
      }
      // 低い太鼓の連打を後半に重ねて溜めを厚くする
      for (let t = end / 2; t < end; t += 0.12) tone(65.41, start + t, 0.25, 0.25);
      hit(start + end, 1);
      crash(start + end, 0.8);
    },
    fanfare: () => {
      const start = context.currentTime;
      // ド・ミ・ソ・ドを順に、最後の和音を伸ばす
      [523.25, 659.25, 783.99].forEach((f, i) => {
        tone(f, start + i * 0.15, 0.3);
      });
      [523.25, 659.25, 783.99, 1046.5].forEach((f) => {
        tone(f, start + 0.5, 1.4);
      });
    },
    grandFanfare: () => {
      const start = context.currentTime;
      crash(start, 0.7);
      // ソ・ド・ミ・ソ と ド・ミ・ソ・ド を 2 段で駆け上がり、オクターブを重ねた和音を長く伸ばす
      [392, 523.25, 659.25, 783.99, 523.25, 659.25, 783.99, 1046.5].forEach((f, i) => {
        tone(f, start + i * 0.11, 0.3);
      });
      [261.63, 523.25, 659.25, 783.99, 1046.5, 1318.51].forEach((f) => {
        tone(f, start + 0.95, 2.6, 0.14);
      });
      crash(start + 0.95, 0.5);
    },
    chime: () => {
      const start = context.currentTime;
      tone(880, start, 0.2);
      tone(1318.51, start + 0.12, 0.35);
    },
    entrance: () => {
      const start = context.currentTime;
      // ソ・ド・ミ・ソと駆け上がる短い登場音
      [392, 523.25, 659.25, 783.99].forEach((f, i) => {
        tone(f, start + i * 0.08, 0.5);
      });
    },
    close: () => {
      void context.close();
    },
  };
}
